import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Plus, Trash2, Edit, Save, X, Copy, ExternalLink,
  GripVertical, FileText, Users, Eye, Link2, Check
} from 'lucide-react';
import axios from 'axios';
import { AdminPageHeader, AdminPageShell, AdminPageBody } from '../../components/AdminPageHeader';
import { BackgroundImagesUploader } from '../../components/admin/BackgroundImagesUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = window.location.origin;

const AdminLeadForms = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [showResponses, setShowResponses] = useState(null);
  const [responses, setResponses] = useState([]);
  const [copiedSlug, setCopiedSlug] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  const defaultQuestions = [
    { id: 'q1', type: 'text', question: 'Qual é o seu nome?', options: [] },
    { id: 'q2', type: 'phone', question: 'Qual seu WhatsApp?', options: [] },
    { id: 'q3', type: 'single_choice', question: 'Quando pretende viajar?', options: ['Próximo mês', 'Em 2-3 meses', 'Em 6 meses', 'Sem data definida'] },
    { id: 'q4', type: 'single_choice', question: 'Qual sua intenção de compra?', options: ['Quero reservar agora', 'Quero mais informações', 'Estou apenas pesquisando'] },
    { id: 'q5', type: 'single_choice', question: 'O que te impede de fechar agora?', options: ['Preço', 'Preciso consultar alguém', 'Preciso de mais detalhes', 'Nada, quero fechar!'] }
  ];

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    active: true,
    linked_package_id: '',
    questions: defaultQuestions,
    final_button_text: 'Entrar no grupo VIP',
    final_button_url: ''
  });

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) { navigate('/admin/login'); return; }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [formsRes, pkgRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/lead-forms/`),
        axios.get(`${BACKEND_URL}/api/packages/?active_only=true`).catch(() => ({ data: [] }))
      ]);
      setForms(formsRes.data || []);
      const pkgs = Array.isArray(pkgRes.data) ? pkgRes.data : [];
      setPackages(pkgs.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')));
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const resetForm = () => {
    setFormData({
      title: '', slug: '', active: true, linked_package_id: '',
      questions: defaultQuestions,
      intro_title: '', intro_subtitle: '', intro_benefits: [''],
      intro_button_text: 'Iniciar', intro_footer_text: 'Leva menos de 45 segundos',
      logo_url: '', background_url: '', background_urls: [], use_package_background: true,
      accent_color: '#007BFF', final_button_color: '#007BFF',
      success_title: 'Recebemos suas informações!',
      success_message: 'Você agora está na lista prioritária.',
      final_button_text: 'Entrar no grupo VIP', final_button_url: '',
      show_step_counter: true
    });
    setEditingForm(null);
  };

  const openEdit = (form) => {
    setEditingForm(form);
    setFormData({
      title: form.title,
      slug: form.slug,
      active: form.active,
      linked_package_id: form.linked_package_id || '',
      questions: form.questions || defaultQuestions,
      intro_title: form.intro_title || '',
      intro_subtitle: form.intro_subtitle || '',
      intro_benefits: form.intro_benefits?.length ? form.intro_benefits : [''],
      intro_button_text: form.intro_button_text || 'Iniciar',
      intro_footer_text: form.intro_footer_text || '',
      logo_url: form.logo_url || '',
      background_url: form.background_url || '',
      background_urls: form.background_urls?.length ? form.background_urls : (form.background_url && /^https?:/i.test(form.background_url) ? [form.background_url] : []),
      use_package_background: form.use_package_background !== false,
      accent_color: form.accent_color || '#007BFF',
      final_button_color: form.final_button_color || '#007BFF',
      success_title: form.success_title || 'Recebemos suas informações!',
      success_message: form.success_message || 'Você agora está na lista prioritária.',
      final_button_text: form.final_button_text || 'Entrar no grupo VIP',
      final_button_url: form.final_button_url || '',
      show_step_counter: form.show_step_counter !== false
    });
    setShowModal(true);
  };

  const openNew = () => { resetForm(); setShowModal(true); };

  const handleSave = async () => {
    if (!formData.title || !formData.slug) {
      setMessage({ type: 'error', text: 'Título e slug são obrigatórios.' });
      return;
    }
    try {
      const payload = {
        ...formData,
        linked_package_id: formData.linked_package_id || null,
        questions: formData.questions.filter(q => q.question.trim()),
        intro_benefits: (formData.intro_benefits || []).filter(b => b.trim()),
        intro_title: formData.intro_title || null,
        intro_subtitle: formData.intro_subtitle || null,
        logo_url: formData.logo_url || null,
        background_url: formData.background_urls?.[0] || null,
        background_urls: (formData.background_urls || []).slice(0, 10)
      };
      if (editingForm) {
        await axios.put(`${BACKEND_URL}/api/lead-forms/${editingForm.id}`, payload);
        setMessage({ type: 'success', text: 'Formulário atualizado!' });
      } else {
        await axios.post(`${BACKEND_URL}/api/lead-forms/`, payload);
        setMessage({ type: 'success', text: 'Formulário criado!' });
      }
      setShowModal(false);
      resetForm();
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao salvar.' });
    }
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Excluir formulário "${form.title}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/lead-forms/${form.id}`);
      setMessage({ type: 'success', text: 'Excluído!' });
      await loadData();
    } catch { setMessage({ type: 'error', text: 'Erro ao excluir.' }); }
  };

  const handleDuplicate = async (form) => {
    try {
      await axios.post(`${BACKEND_URL}/api/lead-forms/${form.id}/duplicate`);
      setMessage({ type: 'success', text: `Formulário "${form.title}" duplicado!` });
      await loadData();
    } catch { setMessage({ type: 'error', text: 'Erro ao duplicar.' }); }
  };

  const toggleActive = async (form) => {
    try {
      await axios.put(`${BACKEND_URL}/api/lead-forms/${form.id}`, { active: !form.active });
      await loadData();
    } catch {}
  };

  const loadResponses = async (form) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/lead-forms/${form.id}/responses`);
      setResponses(res.data || []);
      setShowResponses(form);
    } catch {}
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${SITE_URL}/f/${slug}`);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(''), 2000);
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setFormData(prev => ({ ...prev, questions: newQuestions }));
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, {
        id: `q${Date.now()}`, type: 'text', question: '', options: []
      }]
    }));
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev, questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (qIdx, optIdx, value) => {
    const newQuestions = [...formData.questions];
    const opts = [...(newQuestions[qIdx].options || [])];
    opts[optIdx] = value;
    newQuestions[qIdx] = { ...newQuestions[qIdx], options: opts };
    setFormData(prev => ({ ...prev, questions: newQuestions }));
  };

  const addOption = (qIdx) => {
    const newQuestions = [...formData.questions];
    const opts = [...(newQuestions[qIdx].options || []), ''];
    newQuestions[qIdx] = { ...newQuestions[qIdx], options: opts };
    setFormData(prev => ({ ...prev, questions: newQuestions }));
  };

  const removeOption = (qIdx, optIdx) => {
    const newQuestions = [...formData.questions];
    const opts = (newQuestions[qIdx].options || []).filter((_, i) => i !== optIdx);
    newQuestions[qIdx] = { ...newQuestions[qIdx], options: opts };
    setFormData(prev => ({ ...prev, questions: newQuestions }));
  };

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Formulários de Captação"
        description="Crie e gerencie formulários estilo Typeform para captação de leads"
        Icon={FileText}
        iconClassName="text-orange-500"
        actions={
          <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="new-form-btn">
            <Plus className="w-4 h-4 mr-2" /> Novo Formulário
          </Button>
        }
      />
      <AdminPageBody>
        {message.text && (
          <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {message.text}
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Carregando...</div>
        ) : forms.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Nenhum formulário criado</p>
              <p className="text-sm mt-1">Crie seu primeiro formulário de captação de leads</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {forms.map(form => (
              <Card key={form.id} className={`${!form.active ? 'opacity-60' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg text-gray-900 truncate">{form.title}</h3>
                        <Badge className={form.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}>
                          {form.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Link2 className="w-3 h-3" /> /f/{form.slug}
                        </span>
                        <span>{form.questions?.length || 0} perguntas</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => copyLink(form.slug)}
                        data-testid={`copy-link-${form.slug}`}
                      >
                        {copiedSlug === form.slug ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        <span className="ml-1 hidden sm:inline">{copiedSlug === form.slug ? 'Copiado!' : 'Copiar Link'}</span>
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => window.open(`/f/${form.slug}`, '_blank')}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => loadResponses(form)} data-testid={`view-responses-${form.slug}`}>
                        <Users className="w-4 h-4" />
                        <span className="ml-1 hidden sm:inline">Respostas</span>
                      </Button>
                      <Switch checked={form.active} onCheckedChange={() => toggleActive(form)} />
                      <Button variant="outline" size="sm" onClick={() => openEdit(form)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDuplicate(form)} title="Duplicar formulário">
                        <Copy className="w-4 h-4" />
                        <span className="ml-1 hidden sm:inline">Duplicar</span>
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(form)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      {/* ===== MODAL: Criar/Editar ===== */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingForm ? 'Editar Formulário' : 'Novo Formulário'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            {/* ---- GERAL ---- */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Título interno *</Label>
                <Input value={formData.title} onChange={(e) => { const t = e.target.value; setFormData(prev => ({ ...prev, title: t, slug: !editingForm ? generateSlug(t) : prev.slug })); }} placeholder="Ex: Chapada Diamantina - Junho" />
              </div>
              <div>
                <Label>Slug (URL) *</Label>
                <Input value={formData.slug} onChange={(e) => setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))} placeholder="chapada-junho" />
                <p className="text-xs text-gray-400 mt-1">/f/{formData.slug || '...'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pacote Vinculado (opcional)</Label>
                <Select value={formData.linked_package_id || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, linked_package_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum pacote</SelectItem>
                    {packages.map(pkg => (<SelectItem key={pkg.id} value={pkg.id}>{pkg.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={formData.show_step_counter} onCheckedChange={(v) => setFormData(prev => ({ ...prev, show_step_counter: v }))} />
                  <Label className="text-sm">Mostrar contador (1 de 5)</Label>
                </div>
              </div>
            </div>

            {/* ---- VISUAL ---- */}
            <div className="border-t pt-4 space-y-3">
              <Label className="text-base font-bold">Visual</Label>
              <div>
                <Label>URL da Logo (opcional)</Label>
                <Input value={formData.logo_url} onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))} placeholder="https://..." />
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Switch checked={formData.use_package_background} onCheckedChange={(v) => setFormData(prev => ({ ...prev, use_package_background: v }))} />
                <Label className="text-sm">Usar imagem do pacote automaticamente</Label>
              </div>
              {!formData.use_package_background && (
                <div>
                  <Label>Imagens de fundo (até 10)</Label>
                  <BackgroundImagesUploader value={formData.background_urls} onChange={(urls) => setFormData(prev => ({ ...prev, background_urls: urls }))} />
                </div>
              )}
              {formData.use_package_background && !formData.linked_package_id && (
                <p className="text-xs text-amber-600">Vincule um pacote acima para usar o fundo automático.</p>
              )}

              {/* Cor principal */}
              <div>
                <Label className="text-sm font-semibold">Cor dos botões</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {[
                    { color: '#007BFF', label: 'Azul' },
                    { color: '#003366', label: 'Azul Escuro' },
                    { color: '#FFD700', label: 'Amarelo' },
                    { color: '#0ABAB5', label: 'Turquesa' },
                    { color: '#E63946', label: 'Vermelho' },
                    { color: '#FF6B35', label: 'Laranja' },
                  ].map(c => (
                    <button key={c.color} onClick={() => setFormData(prev => ({ ...prev, accent_color: c.color }))}
                      className={`w-9 h-9 rounded-lg border-2 transition-all hover:scale-110 ${formData.accent_color === c.color ? 'border-gray-900 ring-2 ring-offset-1 ring-gray-400 scale-110' : 'border-gray-200'}`}
                      style={{ backgroundColor: c.color }} title={c.label} />
                  ))}
                  <input type="color" value={formData.accent_color} onChange={(e) => setFormData(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="w-9 h-9 rounded-lg border border-gray-200 cursor-pointer" title="Cor personalizada" />
                </div>
                <p className="text-xs text-gray-400 mt-1">Cor: {formData.accent_color}</p>
              </div>

              {/* Cor do botão final */}
              <div>
                <Label className="text-sm font-semibold">Cor do botão final (tela de sucesso)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {[
                    { color: '#007BFF', label: 'Azul' },
                    { color: '#25D366', label: 'WhatsApp' },
                    { color: '#003366', label: 'Azul Escuro' },
                    { color: '#FFD700', label: 'Amarelo' },
                    { color: '#0ABAB5', label: 'Turquesa' },
                    { color: '#E63946', label: 'Vermelho' },
                  ].map(c => (
                    <button key={c.color} onClick={() => setFormData(prev => ({ ...prev, final_button_color: c.color }))}
                      className={`relative w-9 h-9 rounded-lg border-2 transition-all hover:scale-110 ${formData.final_button_color === c.color ? 'border-gray-900 ring-2 ring-offset-1 ring-gray-400 scale-110' : 'border-gray-200'}`}
                      style={{ backgroundColor: c.color }} title={c.label}>
                      {c.label === 'WhatsApp' && <svg className="w-4 h-4 text-white absolute inset-0 m-auto" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.634-1.215A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.813c-2.188 0-4.219-.588-5.977-1.613l-.429-.254-2.746.72.733-2.677-.279-.443A9.776 9.776 0 012.188 12c0-5.414 4.398-9.813 9.812-9.813S21.813 6.586 21.813 12s-4.399 9.813-9.813 9.813z"/></svg>}
                    </button>
                  ))}
                  <input type="color" value={formData.final_button_color} onChange={(e) => setFormData(prev => ({ ...prev, final_button_color: e.target.value }))}
                    className="w-9 h-9 rounded-lg border border-gray-200 cursor-pointer" title="Cor personalizada" />
                </div>
                <p className="text-xs text-gray-400 mt-1">Cor: {formData.final_button_color}</p>
              </div>
            </div>

            {/* ---- TELA INTRO ---- */}
            <div className="border-t pt-4 space-y-3">
              <Label className="text-base font-bold">Tela Inicial (Intro)</Label>
              <div>
                <Label>Título Principal</Label>
                <Input value={formData.intro_title} onChange={(e) => setFormData(prev => ({ ...prev, intro_title: e.target.value }))} placeholder="Ex: Garanta + de R$ 1.400 de desconto..." />
              </div>
              <div>
                <Label>Subtítulo</Label>
                <Input value={formData.intro_subtitle} onChange={(e) => setFormData(prev => ({ ...prev, intro_subtitle: e.target.value }))} placeholder="Ex: Preencha abaixo e garanta sua vaga" />
              </div>
              <div>
                <Label>Benefícios (lista)</Label>
                <div className="space-y-2">
                  {(formData.intro_benefits || ['']).map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-orange-400 flex-shrink-0" />
                      <Input value={b} onChange={(e) => { const nb = [...(formData.intro_benefits || [''])]; nb[i] = e.target.value; setFormData(prev => ({ ...prev, intro_benefits: nb })); }} placeholder={`Benefício ${i+1}`} className="text-sm" />
                      <Button variant="ghost" size="sm" onClick={() => { const nb = (formData.intro_benefits || []).filter((_, idx) => idx !== i); setFormData(prev => ({ ...prev, intro_benefits: nb.length ? nb : [''] })); }} className="text-red-400 h-7 w-7 p-0"><X className="w-3 h-3" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setFormData(prev => ({ ...prev, intro_benefits: [...(prev.intro_benefits || []), ''] }))} className="text-xs"><Plus className="w-3 h-3 mr-1" /> Benefício</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Texto do Botão Iniciar</Label>
                  <Input value={formData.intro_button_text} onChange={(e) => setFormData(prev => ({ ...prev, intro_button_text: e.target.value }))} placeholder="Iniciar" />
                </div>
                <div>
                  <Label>Texto rodapé</Label>
                  <Input value={formData.intro_footer_text} onChange={(e) => setFormData(prev => ({ ...prev, intro_footer_text: e.target.value }))} placeholder="Leva menos de 45 segundos" />
                </div>
              </div>
            </div>

            {/* ---- PERGUNTAS ---- */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-bold">Perguntas</Label>
                <Button variant="outline" size="sm" onClick={addQuestion}><Plus className="w-3 h-3 mr-1" /> Adicionar</Button>
              </div>
              <div className="space-y-4">
                {formData.questions.map((q, idx) => (
                  <div key={q.id || idx} className="border rounded-lg p-4 bg-gray-50 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-4 h-4 text-gray-400" />
                        <Badge variant="outline" className="text-xs">#{idx + 1}</Badge>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeQuestion(idx)} className="text-red-400 h-7 w-7 p-0"><X className="w-4 h-4" /></Button>
                    </div>
                    <Input value={q.question} onChange={(e) => updateQuestion(idx, 'question', e.target.value)} placeholder="Texto da pergunta" className="font-medium" />
                    <Select value={q.type} onValueChange={(v) => updateQuestion(idx, 'type', v)}>
                      <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Texto livre</SelectItem>
                        <SelectItem value="phone">Telefone</SelectItem>
                        <SelectItem value="single_choice">Escolha única</SelectItem>
                      </SelectContent>
                    </Select>
                    {q.type === 'single_choice' && (
                      <div className="pl-4 space-y-2">
                        <Label className="text-xs text-gray-500">Opções</Label>
                        {(q.options || []).map((opt, optIdx) => (
                          <div key={optIdx} className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 w-5">{String.fromCharCode(65 + optIdx)}</span>
                            <Input value={opt} onChange={(e) => updateOption(idx, optIdx, e.target.value)} placeholder={`Opção ${optIdx + 1}`} className="text-sm" />
                            <Button variant="ghost" size="sm" onClick={() => removeOption(idx, optIdx)} className="text-red-400 h-7 w-7 p-0"><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={() => addOption(idx)} className="text-xs"><Plus className="w-3 h-3 mr-1" /> Opção</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ---- TELA FINAL ---- */}
            <div className="border-t pt-4 space-y-3">
              <Label className="text-base font-bold">Tela Final (Sucesso)</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Título de sucesso</Label>
                  <Input value={formData.success_title} onChange={(e) => setFormData(prev => ({ ...prev, success_title: e.target.value }))} placeholder="Recebemos suas informações!" />
                </div>
                <div>
                  <Label>Mensagem</Label>
                  <Input value={formData.success_message} onChange={(e) => setFormData(prev => ({ ...prev, success_message: e.target.value }))} placeholder="Você está na lista prioritária." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Texto do Botão Final</Label>
                  <Input value={formData.final_button_text} onChange={(e) => setFormData(prev => ({ ...prev, final_button_text: e.target.value }))} placeholder="Ex: Entrar no grupo VIP" />
                </div>
                <div>
                  <Label>URL do Botão</Label>
                  <Input value={formData.final_button_url} onChange={(e) => setFormData(prev => ({ ...prev, final_button_url: e.target.value }))} placeholder="https://chat.whatsapp.com/..." />
                </div>
              </div>
            </div>

            {/* ---- ATIVO + BOTÕES ---- */}
            <div className="flex items-center gap-2">
              <Switch checked={formData.active} onCheckedChange={(v) => setFormData(prev => ({ ...prev, active: v }))} />
              <Label>Formulário Ativo</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1"><X className="w-4 h-4 mr-2" /> Cancelar</Button>
              <Button onClick={handleSave} className="flex-1 bg-[#003366]"><Save className="w-4 h-4 mr-2" /> {editingForm ? 'Atualizar' : 'Criar'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== MODAL: Respostas ===== */}
      <Dialog open={!!showResponses} onOpenChange={() => setShowResponses(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Respostas - {showResponses?.title} ({responses.length})</DialogTitle>
          </DialogHeader>
          {responses.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Nenhuma resposta ainda</p>
          ) : (
            <div className="space-y-3 mt-2">
              {responses.map((resp, idx) => (
                <div key={resp.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-2">
                    <Badge variant="outline">#{responses.length - idx}</Badge>
                    <span className="text-xs text-gray-400">
                      {new Date(resp.created_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {resp.answers?.map((a, aIdx) => {
                      const q = showResponses?.questions?.find(q => q.id === a.question_id);
                      return (
                        <div key={aIdx} className="flex gap-2 text-sm">
                          <span className="text-gray-500 min-w-[120px]">{q?.question || a.question_id}:</span>
                          <span className="font-medium text-gray-900">{a.answer}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </AdminPageBody>
    </AdminPageShell>
  );
};

export default AdminLeadForms;
